<template>
    <md-list-item ref="colorPicker" class="ql-layer-menu-item">
        <md-button class="ql-color-picker-choice" :style="style()">
            <md-icon v-if="!viewableLayer">visibility_off</md-icon>
            <md-icon v-if="!viewable">speaker_notes_off</md-icon>
            <md-icon v-if="viewable && !showNotes">hide_image</md-icon>
            <md-icon v-else></md-icon>
        </md-button>
        <form novalidate @submit.stop.prevent="saveTitle" @keyup.stop.prevent="keyPress" v-if="editable">
            <span v-if="!editing" @click="edit()">{{layer.title}}&nbsp;<md-tooltip md-direction="top">click to edit title</md-tooltip></span>
            <md-input-container v-if="editing">
                <md-input v-model="layer.title" :value="layer.title" @click.stop.prevent></md-input>
            </md-input-container>
        </form>
        <span style="width:100%; padding-left: 15px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;" v-else> {{layer.title}}
            <md-tooltip md-direction="top">No Edit Permissions</md-tooltip>
        </span>
        <md-button class="md-icon-button md-list-action" @click.stop.prevent="saveTitle" v-if="editing">
            <md-icon>save</md-icon>
        </md-button>
        <md-list-expand>
            <md-list class="sub-list">
                <md-list-item class="sub-list-item">
                    <span v-if="viewableLayer" @click="hide">
                        Hide Layer
                    </span>
                    <span v-if="!viewableLayer" @click="show">
                        Show Layer
                    </span>
                </md-list-item>
                <md-list-item class="sub-list-item">
                    <span :class="viewableLayer ? 'show-layer' :'hide-layer'" v-if="showCues" @click="hideCue">
                        Hide Cues
                    </span>
                    <span :class="!viewableLayer ? 'hide-layer' :'show-layer'" v-if="!showCues" @click="showCue">
                        Show Cues
                    </span>
                </md-list-item>
                <md-list-item class="sub-list-item">
                    <span :class="!viewableLayer ? 'hide-layer' :'show-layer'" v-if="showNotes" @click="hideText">
                        Hide Notes
                    </span>
                    <span :class="!viewableLayer ? 'hide-layer' :'show-layer'" v-if="!showNotes" @click="showText">
                        Show Notes
                    </span>
                </md-list-item>
                <md-list-item v-if="editable">
                    Change Color
                    <md-list-expand>
                        <div class="property-box">
                            <div class="property-title">
                                <span class="title">Color</span>
                                <div class="current-color-box" :style="{backgroundColor: this.layer.color}"></div>
                            </div>
                            <div class="property-content note_color">
                                <template v-if="editable">
                                    <div class="color-swatches">
                                        <div class="color-box color-box-black" @click="pickLayerColor('#000000')"></div>
                                        <div class="color-box color-box-red" @click="pickLayerColor('#ff0000')"></div>
                                        <div class="color-box color-box-green" @click="pickLayerColor('#00ff00')"></div>
                                        <div class="color-box color-box-blue" @click="pickLayerColor('#0000ff')"></div>
                                    </div>
                                    <color-picker :value="layer.color != '#000000' ? layer.color : '#FF0000'" @input="pickColor"/>
                                    <md-tooltip md-direction="top">click to change color theme</md-tooltip>
                                </template>
                            </div>
                        </div>
                    </md-list-expand>
                </md-list-item>
                <md-list-item>
                    Edit Cue Properties
                    <md-list-expand>
                        <div class="property-box">
                            <span class="property-title">Cue Placement:</span>
                            <div class="property-content">
                                <md-radio v-model="defaultCuePlacement" mdValue="left" class="md-primary" @change="changeCueReplacement"> Left side of page</md-radio>
                                <md-radio v-model="defaultCuePlacement" mdValue="right" class="md-primary" @change="changeCueReplacement"> Right side of page</md-radio>
                            </div>
                        </div>
                        <div class="property-box">
                            <span class="property-title">Visible in cue:</span>
                            <div class="property-content">
                                <md-checkbox v-model="layerHeaderBox.layerName" @change="changeHeaderBox">Layer Name</md-checkbox>
                                <md-checkbox v-model="layerHeaderBox.title" @change="changeHeaderBox">Number/Title</md-checkbox>
                                <md-checkbox v-model="layerHeaderBox.label" @change="changeHeaderBox">Label</md-checkbox>
                                <md-checkbox v-model="layerHeaderBox.time" @change="changeHeaderBox('time')">Time</md-checkbox>
                            </div>
                        </div>
                        <div class="property-box">
                            <span class="property-title">Cue Size:</span>
                            <vue-slider v-model="titleFontSize" :min="8" :max="24" @change="changeTitleFontSize"></vue-slider>
                        </div>
                        <div class="property-box">
                            <span class="property-title">Resizable Box Opacity:</span>
                            <vue-slider v-model="layerBoxOpacity" @change="changeBoxOpacity"></vue-slider>
                        </div>
                        <div class="property-box">
                            <span class="property-title">Cue Line Opacity:</span>
                            <vue-slider v-model="cueLineOpacity" @change="changeLineOpacity"></vue-slider>
                        </div>
                        <div class="property-box" v-if="editable">
                            <md-switch v-model="autoNumbering" class="md-primary">Auto Numbering</md-switch>
                            <md-tooltip md-direction="top">Applies only to cues you create</md-tooltip>
                            <div class="property-content" v-if="autoNumbering">
                                <md-input-container>
                                    <label>{{ autoNumberFieldLabel }}</label>
                                    <md-input type="number" v-model="autoNumberStart" @input="changeAutoNumberStart" @focus="autoNumberStartFocused = true" @blur="autoNumberStartFocused = false"></md-input>
                                </md-input-container>
                                <md-input-container>
                                    <label>Interval*</label>
                                    <md-input type="number" v-model="autoNumberInterval" @input="changeAutoNumberInterval"></md-input>
                                </md-input-container>
                                <p class="error" v-if="autoNumberSeedInvalid">Enter a number to begin auto-numbering.</p>
                                <p class="error" v-else-if="autoNumberIntervalInvalid">Interval must be a number greater than 0.</p>
                            </div>
                        </div>
                        <div class="property-box" v-if="editable">
                            <span class="property-title">Attributes:</span>
                            <div class="property-content">
                                <div class="property-box" v-for="group in attributeGroups" :key="group.key">
                                    <div class="property-check">
                                        <span class="property-title">{{ group.label }}</span>
                                        <span><md-checkbox v-model="group.checked" @change="changeGroupChecked(group)" :key="group.key">Check All</md-checkbox></span>
                                    </div>
                                    <div class="property-content">
                                        <md-checkbox v-model="attributes[attr]" @change="changeAttributes(attr)" v-for="attr in group.attributes" :key="attr">{{cueAttributes[attr]}}</md-checkbox>
                                    </div>
                                </div>
                                <div class="property-box" key="layerUserDefined">
                                    <div class="property-check">
                                        <span class="property-title">User Defined</span>
                                    </div>
                                    <!-- {{userDefined}} -->
                                    <div class="property-content">
                                        <form class="user-defined-form" v-for="(attr, index) in userDefined" :key="index" novalidate @submit.stop.prevent="saveTitleUserDefined" v-if="editable">
                                            <md-checkbox class="user-defined-checkbox" v-model="attr.value" @change="editUserDefined(index)">
                                            </md-checkbox>
                                            <span v-if="editingActiveUserDefined !== index && attr.label !== ''" class="black" @click="editUserDefined(index)">{{ attr.label }} </span>
                                            <span v-else-if="editingActiveUserDefined !== index && attr.label === ''" class="gray" @click="editUserDefined(index)">User {{ index.replaceAll(/\D+/g, '') }}</span>
                                            <md-tooltip v-if="editingActiveUserDefined !== index" md-direction="top">click to edit title</md-tooltip>
                                            <md-input-container class="user-defined" v-if="editingUserDefined && editingActiveUserDefined === index">
                                                <md-input class="user-defined-input" :id="`user-defined-input-${index}`" v-model="attr.label" :value="attr.label"  @blur="saveTitleUserDefined(index)"></md-input>
                                            </md-input-container>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            <div class="property-action">
                                <md-button class="md-raised md-primary" @click.stop.prevent="uncheckAllAttrs">Uncheck All</md-button>
                            </div>
                        </div>
                    </md-list-expand>
                </md-list-item>
                <md-list-item>
                    Edit Note Properties
                    <md-list-expand>
                        <div class="property-box" v-if="editable && false">
                            <span class="property-title">Default Text Tool:</span>
                            <div class="property-content">
                                <md-radio v-model="layer.defaultTextTool" mdValue="text" class="md-primary" @change="changeTextTool"> Text Tool</md-radio>
                                <md-radio v-model="layer.defaultTextTool" mdValue="highlight" class="md-primary" @change="changeTextTool"> Highlight Tool</md-radio>
                                <md-radio v-model="layer.defaultTextTool" mdValue="line" class="md-primary" @change="changeTextTool"> Line Tool</md-radio>
                                <md-radio v-model="layer.defaultTextTool" mdValue="stamp" class="md-primary" @change="changeTextTool"> Stamp Tool</md-radio>
                                <md-radio v-model="layer.defaultTextTool" mdValue="count" class="md-primary" @change="changeTextTool"> Count Tool</md-radio>
                            </div>
                        </div>
                        <div class="property-box">
                            <div class="md-input-container md-theme-default md-has-select md-has-value">
                                <label for="fontFamily">Default Font Family:</label>
                                <select class="md-select md-theme-default" id="fontFamily" v-model="layer.fontFamily" @change="changeFontFamily" :disabled="!editable" style="border:none;">
                                    <option class="md-list-item md-menu-item md-option" :value="'Arial'">Arial</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Helvetica'">Helvetica</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Roboto'">Roboto</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Times New Roman'">Times New Roman</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Times'">Times</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Courier New'">Courier New</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Courier'">Courier</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Verdana'">Verdana</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Georgia'">Georgia</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Palatino'">Palatino</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Garamond'">Garamond</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Bookman'">Bookman</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Comic Sans MS'">Comic Sans MS</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Trebuchet MS'">Trebuchet MS</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Arial Black'">Arial Black</option>
                                    <option class="md-list-item md-menu-item md-option" :value="'Impact'">Impact</option>
                                </select>
                            </div>
                        </div>
                        <div class="property-box note-property-box">
                            <div class="md-input-container md-theme-default md-has-select md-has-value">
                                <label for="fontSize">Default Font Size:</label>
                                <select class="md-select md-theme-default" id="fontSize" v-model="layer.textFontSize" @change="changeTextFontSize" :disabled="!editable" style="border:none;" aria-label="Default Font Size">
                                    <option class="md-list-item md-menu-item md-option" v-for="size in [8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24]" :value="size" :key="size">
                                        <span class="md-select-value">{{ size }}</span>
                                    </option>
                                </select>
                            </div>
                            <md-button-toggle>
                                <md-button class="md-icon-button" :class="{'md-toggle': layer.defaultBold}" @click="changeTextBold" :disabled="!editable">
                                    <md-icon>format_bold</md-icon>
                                </md-button>
                                <md-button class="md-icon-button" :class="{'md-toggle': layer.defaultItalic}" @click="changeTextItalic" :disabled="!editable">
                                    <md-icon>format_italic</md-icon>
                                </md-button>
                            </md-button-toggle>
                        </div>
                        <div class="property-box">
                            <span class="property-title">Text and Stamp Background</span>
                            <div class="property-content">
                                <md-radio v-model="defaultTextStampBackground" mdValue="solid" class="md-primary" @change="changeTextStampBackground"> Solid</md-radio>
                                <md-radio v-model="defaultTextStampBackground" mdValue="nofill" class="md-primary" @change="changeTextStampBackground"> No Fill</md-radio>
                            </div>
                        </div>
                        <div class="property-box">  
                            <span class="property-title">Default Line Thickness:</span>
                            <vue-slider v-model="layer.lineThikness" :min="1" :max="5" @change="changeLineThikness"></vue-slider>
                        </div>
                        <div class="property-box">
                            <span class="property-title">Default Stamp Size:</span>
                            <vue-slider v-model="layer.stampSize" :min="15" :max="150" @change="changeStampSize"></vue-slider>
                        </div>
                        <div class="property-box">
                            <div class="property-title">
                                <span class="title">Primary Color</span>
                                <div class="current-color-box" :style="{backgroundColor: layer.primaryColor}"></div>
                            </div>
                            <div class="property-content note_color">
                                <template v-if="editable && !layer.matchPrimaryColor">
                                    <div class="color-swatches">
                                        <div class="color-box color-box-black" @click="pickSpecificColor1('#000000')"></div>
                                        <div class="color-box color-box-red" @click="pickSpecificColor1('#ff0000')"></div>
                                        <div class="color-box color-box-green" @click="pickSpecificColor1('#00ff00')"></div>
                                        <div class="color-box color-box-blue" @click="pickSpecificColor1('#0000ff')"></div>
                                    </div>
                                    <color-picker :value="layer.primaryColor != '#000000' ? layer.primaryColor : '#FF0000'"  @input="pickColor1"  />
                                </template>
                                <template v-else>
                                    <div class="color-swatches">
                                        <div class="color-box color-box-black"></div>
                                        <div class="color-box color-box-red"></div>
                                        <div class="color-box color-box-green"></div>
                                        <div class="color-box color-box-blue"></div>
                                    </div>
                                    <color-picker :value="layer.primaryColor" />
                                </template>
                            </div>
                            <div>
                                <md-checkbox v-model="layer.matchPrimaryColor" :disabled="!editable" @change="handleMatchLayerColor('primary')" >Match Layer Color</md-checkbox>
                            </div>
                        </div>
                        <div class="property-box">
                            <div class="property-title">
                                <span class="title">Secondary Color</span>
                                <div class="current-color-box" :style="{backgroundColor: layer.secondaryColor}"></div>
                            </div>
                            <div class="property-content note_color">
                                <template v-if="editable && !layer.matchSecondaryColor">
                                    <div class="color-swatches">
                                        <div class="color-box color-box-black" @click="pickSpecificColor2('#000000')"></div>
                                        <div class="color-box color-box-red" @click="pickSpecificColor2('#ff0000')"></div>
                                        <div class="color-box color-box-green" @click="pickSpecificColor2('#00ff00')"></div>
                                        <div class="color-box color-box-blue" @click="pickSpecificColor2('#0000ff')"></div>
                                    </div>
                                    <color-picker :value="layer.secondaryColor != '#000000' ? layer.secondaryColor : '#FF0000'" @input="pickColor2"/>
                                </template>
                                <template v-else>
                                    <div class="color-swatches">
                                        <div class="color-box color-box-black"></div>
                                        <div class="color-box color-box-red"></div>
                                        <div class="color-box color-box-green"></div>
                                        <div class="color-box color-box-blue"></div>
                                    </div>
                                    <color-picker :value="layer.secondaryColor" />
                                </template>
                            </div>
                            <div>
                                <md-checkbox v-model="layer.matchSecondaryColor" :disabled="!editable" @change="handleMatchLayerColor('secondary')" >Match Layer Color</md-checkbox>
                            </div>
                        </div>
                        <div class="property-box">
                            <div class="property-title">
                                <span class="title">Highlight Color</span>
                                <div class="current-color-box" :style="{backgroundColor: layer.highlightColor}"></div>
                            </div>
                            <div class="property-content note_color">
                                <template v-if="editable && !layer.matchHighlightColor">
                                    <div class="color-swatches">
                                        <div class="color-box color-box-black" @click="pickSpecificColor3('#000000')"></div>
                                        <div class="color-box color-box-red" @click="pickSpecificColor3('#ff0000')"></div>
                                        <div class="color-box color-box-green" @click="pickSpecificColor3('#00ff00')"></div>
                                        <div class="color-box color-box-blue" @click="pickSpecificColor3('#0000ff')"></div>
                                    </div>
                                    <color-picker :value="layer.highlightColor != '#000000' ? layer.highlightColor : '#FF0000'" @input="pickColor3"/>
                                </template>
                                <template v-else>
                                    <div class="color-swatches">
                                        <div class="color-box color-box-black"></div>
                                        <div class="color-box color-box-red"></div>
                                        <div class="color-box color-box-green"></div>
                                        <div class="color-box color-box-blue"></div>
                                    </div>
                                    <color-picker :value="layer.highlightColor" />
                                </template>
                            </div>
                            <div>
                                <md-checkbox v-model="layer.matchHighlightColor" :disabled="!editable" @change="handleMatchLayerColor('highlight')" >Match Layer Color</md-checkbox>
                            </div>
                        </div>
                    </md-list-expand>
                </md-list-item>

                <md-list-item @click="deleteLayer" v-if="editable && !this.project.isSample">
                    Delete this layer
                    <md-icon>delete</md-icon>
                </md-list-item>
            </md-list>
        </md-list-expand>
    </md-list-item>
</template>

<script>
/**
 * https://github.com/xiaokaike/vue-color/blob/master/src/components/Swatches.vue
 */

import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import { Slider } from 'vue-color';
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';
import Utils from '@/lib/Utils';

const LayersLayer = {
    name: 'LayersLayer',
    props: ['layer', 'viewable', 'viewableLayer', 'editable', 'showNotes', 'showCues', 'layerTitleFontSize', 'headerBox', 'userDefined', 'layerDefaultCuePlacement', 'layerLineOpacity', 'layerCueBoxOpacity', 'layerAutoNumbering', 'layerAutoNumberStart', 'layerAutoNumberInterval'],
    data: function() {
        return {
            editing: false,
            editingUserDefined: false,
            editingActiveUserDefined: '',
            oldTitle: this.$props.layer.title,
            defaultCuePlacement: this.$props.layerDefaultCuePlacement,
            defaultCuePlacementTimer: null,
            defaultTextStampBackground: this.layer.defaultTextStampBackground,
            defaultTextStampBackgroundTimer: null,
            titleFontSize: this.$props.layerTitleFontSize,
            titleFontSizeTimer: null,
            layerBoxOpacity: this.$props.layerCueBoxOpacity,
            changeBoxOpacityTimer: null,
            cueLineOpacity: this.$props.layerLineOpacity,
            changeLineOpacityTimer: null,
            layerHeaderBox: { time: false, layerName: true, ...(this.$props.headerBox || { title: true, label: true }) },
            changeBackgroundOpacityTimer: null,
            autoNumbering: !!this.$props.layerAutoNumbering,
            autoNumberStart: this.$props.layerAutoNumberStart != null ? this.$props.layerAutoNumberStart : '',
            autoNumberInterval: this.$props.layerAutoNumberInterval != null ? this.$props.layerAutoNumberInterval : 1,
            autoNumberStartTimer: null,
            autoNumberIntervalTimer: null,
            autoNumberStartFocused: false,
            attributes: { 
                ...Object.keys(Utils.cueAttributes).reduce((attrs, attr) => ({...attrs, [attr]: false}), {}), 
                ...(this.$props.layer.attributes ? this.$props.layer.attributes : {}), 
                label: true
            },
            cueAttributes: Utils.cueAttributes,
            attributeGroups: [
                {
                    key: 'lighting',
                    label: 'Lighting',
                    checked: false,
                    attributes: [
                        'notes', 'description', 'parts', 'time', 'focus', 'color', 'beam', 'mark', 'duration', 'followHang', 'block', 'assert', 'link', 'loop', 'curve', 'externalLinks'
                    ]
                },
                {
                    key: 'sound',
                    label: 'Sound',
                    checked: false,
                    attributes: [
                        'target', 'preWait', 'action', 'postWait', 'placement', 'purpose', 'followMode', 'routing', 'trigger'
                    ]
                },
                {
                    key: 'props',
                    label: 'Props',
                    checked: false,
                    attributes: [
                        'propName', 'propQuantity', 'preset', 'whoEnter', 'whereEnter', 'whoExit', 'whereExit', 'exitPage', 'propNotes', 'consumable', 'rehearsal', 'rehearsalSource', 'actual', 'actualSource'
                    ]
                }
            ]
        };
    },
    computed: {
        ...mapGetters([
            'cuePlacement',
            'project',
            'cuesPerLayer',
            'nextCueNumberForLayer'
        ]),
        autoNumberSeedInvalid() {
            return this.autoNumbering && !Utils.isValidCueNumberSeed(this.autoNumberStart);
        },
        autoNumberIntervalInvalid() {
            return this.autoNumbering && this.autoNumberInterval !== '' && !Utils.isValidCueInterval(this.autoNumberInterval);
        },
        // Once the layer has any numbered cue, the field holds the NEXT number in
        // the sequence rather than the initial seed, so relabel it accordingly.
        autoNumberFieldLabel() {
            const numbered = this.cuesPerLayer(this.layer.id)
                .some(cue => cue.title != null && !Number.isNaN(Number.parseFloat(cue.title)));
            return numbered ? 'Next Number*' : 'Starting Number*';
        }
    },
    watch: {
        'layer.attributes': {
            handler: function(after, before) {
                for (const key of Object.keys(Utils.cueAttributes)) {
                    if (after[key] !== before[key]) {
                        this.attributes[key] = after[key];
                    }
                }
            },
            deep: true
        },
        // Persist the toggle from a watcher, not md-switch @change: vue-material
        // fires @change before v-model updates the data, so reading
        // this.autoNumbering in the handler saved the stale (pre-toggle) value.
        autoNumbering: function(val) {
            // Seed the starting number when the switch goes ON with the field
            // empty, rather than letting auto-numbering sit enabled and silently
            // do nothing. One interval past the layer's highest existing cue
            // number, or 1 if it has none.
            //
            // Josh, 14 Sept 2026, choosing that over a flat 1: turning this on
            // mid-show should CONTINUE the sequence, not restart it into a
            // collision with cues that already exist.
            //
            // Only fills a blank. A seed the user typed is never overwritten,
            // and clearing the field afterwards still leaves it invalid -- which
            // the toast on cue creation now reports.
            if (val && !Utils.isValidCueNumberSeed(this.autoNumberStart)) {
                this.autoNumberStart = this.nextCueNumberForLayer(this.layer.id);
                this.changeAutoNumberStart();
            }
            this.changeAutoNumbering();
        },
        // The stored "next number" advances externally as cues are created
        // (addCue writes it). Reflect that live in the field — but not while the
        // user is editing it, so a manual override in progress isn't clobbered.
        layerAutoNumberStart: function(val) {
            if (!this.autoNumberStartFocused) {
                this.autoNumberStart = val != null ? val : '';
            }
        }
    },
    methods: {
        edit() {
            if (this.editable) {
                this.editing = true;
            }
        },
        done() {
            this.editing = false;
        },
        style() {
            let val = 'background-color: white;';
            if (this.layer.color) {
                val = 'background-color: ' + this.layer.color + ' !important;';
            }
            return val;
        },
        keyPress(e) {
            if (e.keyCode === 27) {
                this.editing = false;
                this.$props.layer.title = this.oldTitle;
            }
        },
        saveTitle() {
            this.editing = false;
            this.oldTitle = this.$props.layer.title;
            this.debouncedUpdateLayer();
        },
        editUserDefined(attr) {
            this.editingUserDefined = true;
            this.editingActiveUserDefined = attr;
            if (this.editingActiveUserDefined === attr) {
                setTimeout(() => {
                    document.querySelector(`#user-defined-input-${attr}`).focus();
                }, 100);
            }
        },
        keyPressUserDefined(e) {
            if (e.keyCode === 27) {
                this.editingUserDefined = false;
                this.editingActiveUserDefined = '';
            }
        },
        saveTitleUserDefined() {
            this.editingUserDefined = false;
            this.editingActiveUserDefined = '';
            this.debouncedSetLayerUserDefined();
        },
        pickColor(e) {
            this.layer.color = (e && e.hex) || '';
            if (this.layer.matchPrimaryColor) {
                this.layer.primaryColor = this.layer.color;
            }
            if (this.layer.matchSecondaryColor) {
                this.layer.secondaryColor = this.layer.color;
            }
            if (this.layer.matchHighlightColor) {
                this.layer.highlightColor = this.layer.color;
            }
            this.debouncedUpdateLayer();
        },
        pickColor1(e) {
            this.layer.primaryColor = (e && e.hex) || '';
            this.debouncedUpdateLayer();
        },
        pickColor2(e) {
            this.layer.secondaryColor = (e && e.hex) || '';
            this.debouncedUpdateLayer();
        },
        pickColor3(e) {
            this.layer.highlightColor = (e && e.hex) || '';
            this.debouncedUpdateLayer();
        },
        deleteLayer() {
            this.$emit('deleteLayer', this.layer);
        },
        toggleVisibility() {
            if (this.viewable) {
                this.hide();  
            } else {
                this.show();
            }
        },
        show() {
            this.$store.dispatch('showLayer', this.layer);
        },
        hide() {
            this.$store.dispatch('hideLayer', this.layer);
        },
        showText() {
            if (this.viewableLayer) {
                this.$store.dispatch('showNotes', this.layer);
            }
        },
        hideText() {
            if (this.viewableLayer) {
                this.$store.dispatch('hideNotes', this.layer);
            }
        },
        showCue() {
            if (this.viewableLayer) {
                this.$store.dispatch('showCues', this.layer);
            }
        },
        hideCue() {
            if (this.viewableLayer) {
                this.$store.dispatch('hideCues', this.layer);
            }
        },
        changeCueMarker() {
            this.debouncedUpdateLayer();
        },
        changeCueReplacement() {
            if (this.defaultCuePlacementTimer) {
                clearTimeout(this.defaultCuePlacementTimer);
            }
            this.defaultCuePlacementTimer = setTimeout(() => {
                this.$store.dispatch('setDefaultCuePlacement', {
                    layer: this.layer,
                    defaultCuePlacement: this.defaultCuePlacement
                });
                this.defaultCuePlacementTimer = null;
            }, 500);
        },
        changeTextStampBackground() {
            if (this.defaultTextStampBackgroundTimer) {
                clearTimeout(this.defaultTextStampBackgroundTimer);
            }
            this.defaultTextStampBackgroundTimer = setTimeout(() => {
                this.$store.dispatch('setDefaultTextStampBackground', {
                    layer: this.layer,
                    defaultTextStampBackground: this.defaultTextStampBackground
                });
                this.defaultTextStampBackgroundTimer = null;
            }, 500);
        },
        changeTitleFontSize() {
            if (this.titleFontSizeTimer) {
                clearTimeout(this.titleFontSizeTimer);
            }
            this.titleFontSizeTimer = setTimeout(() => {
                this.$store.dispatch('setLayerTitleFontSize', {
                    layer: this.layer,
                    titleFontSize: this.titleFontSize
                });
                this.titleFontSizeTimer = null;
            }, 500);
        },
        changeBoxOpacity() {
            if (this.changeBoxOpacityTimer) {
                clearTimeout(this.changeBoxOpacityTimer);
            }
            this.changeBoxOpacityTimer = setTimeout(() => {
                this.$store.dispatch('setLayerBoxOpacity', {
                    layer: this.layer,
                    boxOpacity: this.layerBoxOpacity
                });
                this.changeBoxOpacityTimer = null;
            }, 500);
        },
        changeLineOpacity() {
            if (this.changeLineOpacityTimer) {
                clearTimeout(this.changeLineOpacityTimer);
            }
            this.changeLineOpacityTimer = setTimeout(() => {
                this.$store.dispatch('setLayerLineOpacity', {
                    layer: this.layer,
                    lineOpacity: this.cueLineOpacity
                });
                this.changeLineOpacityTimer = null;
            }, 500);
        },
        changeBackgroundOpacity() {
            if (this.changeBackgroundOpacityTimer) {
                clearTimeout(this.changeBackgroundOpacityTimer);
            }
            this.changeBackgroundOpacityTimer = setTimeout(() => {
                this.$store.dispatch('updateLayer', this.layer);
            }, 500);
        },
        changeAutoNumbering() {
            this.$store.dispatch('setLayerAutoNumbering', {
                layer: this.layer,
                autoNumbering: this.autoNumbering
            });
        },
        changeAutoNumberStart() {
            if (this.autoNumberStartTimer) {
                clearTimeout(this.autoNumberStartTimer);
            }
            this.autoNumberStartTimer = setTimeout(() => {
                // Persist the seed as a number when valid, otherwise null so
                // auto-numbering stays a no-op until a valid seed is entered.
                const value = Utils.isValidCueNumberSeed(this.autoNumberStart) ? Number(this.autoNumberStart) : null;
                this.$store.dispatch('setLayerAutoNumberStart', {
                    layer: this.layer,
                    autoNumberStart: value
                });
                this.autoNumberStartTimer = null;
            }, 500);
        },
        changeAutoNumberInterval() {
            if (this.autoNumberIntervalTimer) {
                clearTimeout(this.autoNumberIntervalTimer);
            }
            this.autoNumberIntervalTimer = setTimeout(() => {
                // Only persist a valid interval (> 0); leave the stored value
                // (default 1) untouched while the field is invalid/blank.
                if (Utils.isValidCueInterval(this.autoNumberInterval)) {
                    this.$store.dispatch('setLayerAutoNumberInterval', {
                        layer: this.layer,
                        autoNumberInterval: Number(this.autoNumberInterval)
                    });
                }
                this.autoNumberIntervalTimer = null;
            }, 500);
        },
        changeAttributes(attr) {
            this.$nextTick(() => {
                // RESOLVED A: unchecking the Time attribute also hides Time from the cue.
                // (Checking the Time attribute leaves the Visible-in-cue Time toggle untouched.)
                if (attr === 'time' && !this.attributes.time && this.layerHeaderBox.time) {
                    this.layerHeaderBox.time = false;
                    this.debouncedSetLayerHeaderBox();
                }
                this.debouncedUpdateLayerAttributes();
            });
        },
        changeHeaderBox(field) {
            this.$nextTick(() => {
                // Selection requirement applies only to title/label; Time is independent.
                if (!this.layerHeaderBox.title && !this.layerHeaderBox.label) {
                    this.layerHeaderBox.title = true;
                }
                // RESOLVED A: checking Visible-in-cue Time auto-enables the Time attribute.
                // (Unchecking it leaves the attribute untouched.)
                if (field === 'time' && this.layerHeaderBox.time && !this.attributes.time) {
                    this.attributes.time = true;
                    this.debouncedUpdateLayerAttributes();
                }
                this.debouncedSetLayerHeaderBox();
            });
        },
        checkAllAttrs() {
            this.attributes = {...Object.keys(Utils.cueAttributes).reduce((attrs, attr) => ({...attrs, [attr]: true}), {})};
            this.$nextTick(() => {
                this.debouncedUpdateLayerAttributes();
            });
        },
        uncheckAllAttrs() {
            this.attributes = {...Object.keys(Utils.cueAttributes).reduce((attrs, attr) => ({...attrs, [attr]: false}), {}), label: true, workNote: true};
            for (const group of this.attributeGroups) {
                group.checked = false;
            }
            for (const [, value] of Object.entries(this.userDefined)) {
                value.value = false;
            }
            this.$nextTick(() => {
                this.debouncedSetLayerUserDefined();
            });
            this.$nextTick(() => {
                this.debouncedUpdateLayerAttributes();
            });
        },
        changeGroupChecked(group) {
            this.$nextTick(() => {
                if (group.checked) {
                    this.attributes = {...this.attributes, ...group.attributes.reduce((attrs, attr) => ({...attrs, [attr]: true}), {})};
                    this.$nextTick(() => {
                        this.debouncedUpdateLayerAttributes();
                    });
                } else {
                    this.attributes = {...this.attributes, ...group.attributes.reduce((attrs, attr) => ({...attrs, [attr]: false}), {})};
                    this.$nextTick(() => {
                        this.debouncedUpdateLayerAttributes();
                    });
                }
            });
        },
        changeTextTool() {
            this.debouncedUpdateLayer();
        },
        pickLayerColor(color) {
            this.layer.color = color;
            if (this.layer.matchPrimaryColor) {
                this.layer.primaryColor = this.layer.color;
            }
            if (this.layer.matchSecondaryColor) {
                this.layer.secondaryColor = this.layer.color;
            }
            if (this.layer.matchHighlightColor) {
                this.layer.highlightColor = this.layer.color;
            }
            this.debouncedUpdateLayer();
        },
        pickSpecificColor1(color) {
            this.layer.primaryColor = color;
            this.debouncedUpdateLayer();
        },
        pickSpecificColor2(color) {
            this.layer.secondaryColor = color;
            this.debouncedUpdateLayer();
        },
        pickSpecificColor3(color) {
            this.layer.highlightColor = color;
            this.debouncedUpdateLayer();
        },
        changeFontFamily() {
            this.debouncedUpdateLayer();
        },
        changeTextFontSize() {
            this.debouncedUpdateLayer();
        },
        changeStampSize(e) {
            this.layer.stampSize = e;
            this.debouncedUpdateLayer();
        },
        changeLineThikness(e) {
            this.layer.lineThikness = e;
            this.debouncedUpdateLayer();
        },
        changeTextBold() {
            this.$nextTick(() => {
                this.$store.dispatch('updateLayer', {
                    ...this.layer,
                    defaultBold: !this.layer.defaultBold
                });
            });
        },
        changeTextItalic() {
            this.$nextTick(() => {
                this.$store.dispatch('updateLayer', {
                    ...this.layer,
                    defaultItalic: !this.layer.defaultItalic
                });
            });
        },

        updateLayer() {
            this.$store.dispatch('updateLayer', this.layer);
        },
        setLayerHeaderBox() {
            this.$store.dispatch('setLayerHeaderBox', {
                layer: this.layer,
                headerBox: this.layerHeaderBox
            });
        },
        setLayerUserDefined() {
            this.$store.dispatch('setLayerUserDefined', {
                layer: this.layer,
                userDefined: this.userDefined
            });
        },
        updateLayerAttributes() {
            this.$store.dispatch('updateLayerAttributes', {
                layer: this.layer,
                attributes: this.attributes
            });
        },
        handleMatchLayerColor(type) {
            if (this.editable) {
                if (type === 'primary') {
                    this.layer.matchPrimaryColor = !this.layer.matchPrimaryColor;
                    if (this.layer.matchPrimaryColor) {
                        this.layer.primaryColor = this.layer.color;
                    }
                }
                if (type === 'secondary') {
                    this.layer.matchSecondaryColor = !this.layer.matchSecondaryColor;
                    if (this.layer.matchSecondaryColor) {
                        this.layer.secondaryColor = this.layer.color;
                    }
                }
                if (type === 'highlight') {
                    this.layer.matchHighlightColor = !this.layer.matchHighlightColor;
                    if (this.layer.matchHighlightColor) {
                        this.layer.highlightColor = this.layer.color;
                    }
                }
                this.debouncedUpdateLayer();
            }
        }
    },
    components: {
        'color-picker': Slider,
        'vue-slider': VueSlider
    },
    created: function() {
        let willUpdate = false;
        if (!this.layer.defaultCueMarker) {
            this.$props.layer.defaultCueMarker = 'position';
            willUpdate = true;
        }
        if (!this.layer.titleFontSize) {
            this.$props.layer.titleFontSize = 14;
            willUpdate = true;
        }
        if (!this.layer.defaultCuePlacement) {
            this.$props.layer.defaultCuePlacement = this.cuePlacement === 'right' ? 'right' : 'left';
            willUpdate = true;
        }
        if (!this.layer.defaultTextStampBackground) {
            this.$props.layer.defaultTextStampBackground = 'nofill';
            willUpdate = true;
        }
        if (!this.layer.attributes) {
            this.$props.layer.attributes = {...Object.keys(Utils.cueAttributes).reduce((attrs, attr) => ({...attrs, [attr]: false}), {}), label: true, workNote: true};
            willUpdate = true;
        }
        if (!this.layer.defaultTextTool) {
            this.$props.layer.defaultTextTool = 'text';
            willUpdate = true;
        }
        if (!this.layer.textFontSize) {
            this.$props.layer.textFontSize = 10;
            willUpdate = true;
        }
        if (willUpdate) {
            this.$nextTick(() => {
                this.$store.dispatch('updateLayer', this.layer);
            });
        }

        this.debouncedUpdateLayer = debounce(this.updateLayer, 1000);
        this.debouncedSetLayerHeaderBox = debounce(this.setLayerHeaderBox, 1000);
        this.debouncedSetLayerUserDefined = debounce(this.setLayerUserDefined, 1000);
        this.debouncedUpdateLayerAttributes = debounce(this.updateLayerAttributes, 1000);
    }
};
export default LayersLayer;

</script>

<style lang="scss">
.vc-compact {
    margin: 10px 0;
}
.ql-color-picker {
    width: 100%;
    display: block;
    clear: both;
}
.ql-color-picker-choice {
    margin: 0;
    padding: 0;
    width: 25px;
    height: 25px;
    min-height: 25px;
    min-width: 25px;
    line-height: 25px;
    box-sizing: border-box;
    border: 1px solid black !important;
}
.ql-layer-menu-item {
    form {
        z-index: 100;
        text-align: left;
        width: calc(100% - 1em);
        margin-left: 1em;
    }
    .sub-list {
        .md-list-item .md-list-item-container > .md-icon:first-child {
            margin-right: 0px;
        }
        margin-left: 16px;
        width: calc(100% - 16px);
    }
    .note_color {
        display: flex;
        align-items: center;
        .vc-slider {
            flex: 1;
        }
    }
    .color-swatches {
        flex: 1 0 20%;
        max-width: 80px;
        display: flex;
        flex-wrap: wrap;
        .color-box {
            display: block;
            width: 32px;
            height: 32px;
            cursor: pointer;
            &.color-box-black {
                background: #000;
                border: double 4px #fff;
            }
            &.color-box-red {
                background: #f00;
                border: double 4px #fff;
            }
            &.color-box-green {
                background: #0f0;
                border: double 4px #fff;
            }
            &.color-box-blue {
                background: #00f;
                border: double 4px #fff;
            }
        }
    }
}
.property-box {
    padding: 0 16px 12px;
    .property-check{
        display: flex;
        justify-content: space-between;
        span {
            width: 50%;
        }
        .property-title {
            margin-right: 5px;
            margin-right: 0;
        }
    }
    .property-title {
        display: flex;
        align-items: center;
        .title {
            padding-right: 12px;
        }
        .current-color-box {
            width: 25px;
            height: 25px;
            border: solid 1px black;
            border-radius: 4px;
        }
    }
    .property-content {
        margin: 8px 0;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        .user-defined-form{
            margin: 0px;
            .user-defined-checkbox{
                flex: unset;
                &.md-checked {
                    width: auto;
                }
                
                label.md-checkbox-label {
                    width: 75%;
                }
                span {
                    position: relative;

                    &.gray {
                        color: #7e7676;
                    }
                    &.black {
                        color: #000;
                    }
                    &::after {
                        content: '';
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        width: 100%;
                        height: 1px;
                        background-color: rgba(0, 0, 0, .87);
                    }
                }
            }
            .user-defined{
                margin: 0px 0px 0px 8px;
                padding: 0px;
                max-height: 20px;
                min-height: 20px;
                color: #000;
                .user-defined-input{
                    font-size: 14px;
                    height: 20px;
                    color: #000;
                }
            }
        }
        .md-radio {
            width: 100%;
            margin: 4px 0;
        }
        .md-checkbox {
            margin: 4px 0;
            flex: 1 0 50%;
            label {
                white-space: nowrap;
            }
        }
        .property-box {
            padding-left: 6px;
            padding-right: 0;
            width: 100%;
        }
    }
    .property-action {
        display: flex;
        justify-content: space-between;
        align-items: center;
        .md-button {
            margin-left: 0;
            margin-right: 0;
        }
    }
    #fontFamily {
        z-index: 0;
        position: relative;
    }
}
.checkbox-list {
    display: flex;
    flex-direction: column;
    .checkbox-list-label {
        margin-bottom: 8px;
    }
    .md-checkbox {
        margin: 4px 0;
    }
}
.note-property-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .md-input-container {
        flex: 1;
        margin-right: 12px;
    }
    .md-button-toggle {
        flex: 0 0 80px;
    }
}
.user-defined-form{
    text-align: left;
    width: 49%;
    display: flex;
    align-items: center;
}

.md-theme-show-mode form.user-defined-form > span {
    color:#fff;
}

form.user-defined-form > span {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-left: 8px;
    line-height: normal;
    height: auto;
    position: relative;
    padding-bottom: 2px;
    white-space: nowrap;
}
.md-theme-show-mode form.user-defined-form > span.gray,
form.user-defined-form > span.gray {
    color: gray;
}
form.user-defined-form > span::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: #000;
}

.user-defined-form .user-defined-input {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: normal;
    height: auto;
    position: relative;
    padding-bottom: 2px;
    white-space: nowrap;
}
.sub-list-item .md-list-item-container {
    align-items: stretch;
}
.sub-list-item .md-list-item-container span {
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 0 16px;
}
span.hide-layer {
    opacity: 0.4;
    cursor: not-allowed;
}
</style>


// WEBPACK FOOTER //
// layer.vue?59037094